/**
 * OMON-308: where the "now" marker goes — pure arithmetic, no Chart.js import.
 *
 * Split out for the same reason as trajectory-window.ts and chart-sync.ts: the
 * zoom plugin drags in hammerjs, which needs a DOM, so anything importing
 * chart-kit can't be unit-tested in node.
 *
 * WHY A MARKER AT ALL. The convention across plan-vs-actual charts (Gantt
 * tools) and live time-series (Grafana, and our own Connect swimlane) is the
 * same rule wearing two coats: you need a "today" marker exactly when today is
 * NOT at an edge of the chart. A live tail whose right edge IS now doesn't need
 * one — the edge carries the meaning. A goal chart with future space on screen
 * does, because otherwise nothing separates what happened from what is planned,
 * and a green line that stops three weeks ago looks identical to one that stops
 * today.
 *
 * OMON-308 made this acute rather than merely nice: the chart now opens centred
 * on today, and without a marker the centring is invisible.
 */

export interface MarkerView {
  min: number;
  max: number;
}

/**
 * Position of `now` within the visible range as a fraction 0..1, or null when
 * it is off-screen.
 *
 * Returning null rather than clamping to an edge is deliberate, and copied from
 * the Connect swimlane (timeline-renderer.ts, drawNowMarker): a marker pinned to
 * the edge of a window that has been panned into the past would assert that
 * today is somewhere it isn't. Absent is honest; clamped is a lie.
 *
 * A fraction rather than a pixel because Chart.js's `type: "time"` scale is
 * linear in time (unlike "timeseries"), so this is exact — and it keeps the one
 * source of truth for the position in a module that can be tested.
 */
export function nowMarkerFraction(view: MarkerView, now: number): number | null {
  const span = view.max - view.min;
  if (!(span > 0)) return null;
  if (now < view.min || now > view.max) return null;
  return (now - view.min) / span;
}

/**
 * Whether there is room to print the "now" label without it running off the
 * right-hand edge. The line still draws when this is false — losing the label
 * is a smaller loss than losing the reference point.
 */
export function nowLabelFits(fraction: number): boolean {
  return fraction < 0.92;
}
